import ModalErrorBoundary from '@UI/modals/ModalErrorBoundary';
import { type ContentHref, ContentType } from '@generalTypes/apiTypes';
import { Modal } from '@kathondvla/react-shared-components/src/components';
import { getAngularService } from '@kathondvla/react-shared-components/src/helpers/angularReactHelper';
import type { EditAsideSources } from '@nodeTypeConfig/configTypes';
import { getResourceKey } from '@store/helpers/documentHelpers';
import { any } from 'prop-types';
import type React from 'react';
import { useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as DOCUMENT_ACTIONS from '../../../../reduxLoop/actions/documentActions';
import { useEditHref } from '../../../hooks/UseEditHref';
import AsideRelationsPanel from '../asideRelationsPanel/AsideRelationsPanel';
import { AsideSourceItem } from './AsideSourceItem';
import SourceModal from './SourceModal';
import { selectRelationsToSources } from './asideSourcesSelectors';

const AsideSources: React.FC<{
  config: EditAsideSources;
  readonly: boolean;
}> = ({ config, readonly }) => {
  const dispatch = useDispatch();

  const currentEditingHref = useEditHref();

  const relations = useSelector(selectRelationsToSources);

  const confirmationService = getAngularService('ConfirmationService');
  const sourceModalRef = useRef<Modal>();

  const onDelete = useCallback(
    async (item) => {
      const confirmed = await confirmationService.confirmDeletion();
      if (confirmed) {
        dispatch(DOCUMENT_ACTIONS.removeNodeAction(getResourceKey(item.from.href)));
      }
    },
    [confirmationService, dispatch],
  );

  const onCreate = () => {
    sourceModalRef.current?.open({
      onSubmit: (source) => {
        if (source) {
          dispatch(
            DOCUMENT_ACTIONS.addNodeToParentNodeAction(
              getResourceKey(currentEditingHref),
              1,
              ContentType.SOURCE,
              {
                title: source.title,
              },
            ),
          );
        }
      },
    });
  };

  return (
    <>
      <AsideRelationsPanel
        title={config.label}
        items={relations}
        readOnly={readonly}
        onCreate={onCreate}
        onDelete={onDelete}
      >
        {(item) => <AsideSourceItem href={item.from.href as ContentHref} />}
      </AsideRelationsPanel>
      <Modal
        ref={sourceModalRef}
        size="sm"
        component={<SourceModal />}
        errorBoundaryComponent={ModalErrorBoundary}
      />
    </>
  );
};

AsideSources.displayName = 'AsideSources';

AsideSources.propTypes = {
  config: any,
  readonly: any,
};

export default AsideSources;
